/**
 * MV8 (2026-10-08, R-008 `hidden-creature` / hidden mover): the stealth v2 AWARENESS hook of the legacy per-foot walk — the twin of
 * ft_stealth_check_along_step() (includes/functions/janus/encounter/stealth_awareness.php) on the walk's legacy feet. Pure: geometry,
 * senses, the round's contest cache and the dice all come from readers (websocket/stealth-walk-truth.ts binds them on the page).
 *
 * Per foot (legacyStepPositions, foot 1 = the first foot the walk TRIES), with the mover placed at that foot, roster order:
 *   (a) the mover is Hidden and `other` may observe it (ft_stealth_observer_eligible) → evaluate (other → mover); the FIRST spotter in
 *       roster order wins and every later pair of that foot is skipped (PHP's deliberate short-circuit);
 *   (b) `other` is Hidden and the mover may observe it → evaluate (mover → other); every spotted subject is collected.
 * A spotted mover halts the walk ("You have been spotted — movement halted!"); spotted others halt it too ("You notice something hidden
 * nearby — movement paused!"). The halting foot is NOT taken: the walk ends on the foot before (the adapter cuts it there).
 * evaluate = ft_stealth_evaluate_pair: edge distance → band (+4 ≤ 15 / +2 ≤ 30 / 0 ≤ 45 / −2 ≤ 60, none beyond 60), 9-ray cover → CLOSED at
 * every ray blocked (no contest), heavy ≥ 2/3 → −6, partial ≥ 1/3 → −3, the observer's senses (deafened AND cannot see → no contest); then
 * the round's cached contest or a fresh one (Sneak first, then Attention at the light-driven DIS); spotted = Attention + band + cover > Sneak.
 */
export type Row = Record<string, unknown>;
export const STEALTH_ZONE_FT = 60;
export const STEALTH_COVER_PARTIAL_MOD = -3;
export const STEALTH_COVER_HEAVY_MOD = -6;
export const STEALTH_MOVER_SPOTTED_MESSAGE = 'You have been spotted — movement halted!';
export const STEALTH_OTHERS_SPOTTED_MESSAGE = 'You notice something hidden nearby — movement paused!';

/** ft_stealth_distance_mod(): null = outside the 60 ft zone (no contest). */
export function stealthDistanceMod(dist: number): number | null {
  if (dist > STEALTH_ZONE_FT) return null;
  if (dist <= 15) return 4;
  if (dist <= 30) return 2;
  if (dist <= 45) return 0;
  return -2;
}
export interface StealthCover { closed: boolean; mod: number; tier: 'none' | 'partial' | 'heavy' | 'blocked'; blocked: number; total: number }
/** ft_stealth_cover()'s tiers from the 9-ray sample counts (total 0 = an unshaped encounter: honestly no cover). */
export function stealthCover(blocked: number, total: number): StealthCover {
  if (total <= 0) return { closed: false, mod: 0, tier: 'none', blocked: 0, total: 0 };
  if (blocked >= total) return { closed: true, mod: 0, tier: 'blocked', blocked, total };
  const fraction = blocked / total;
  if (fraction >= 2 / 3) return { closed: false, mod: STEALTH_COVER_HEAVY_MOD, tier: 'heavy', blocked, total };
  if (fraction >= 1 / 3) return { closed: false, mod: STEALTH_COVER_PARTIAL_MOD, tier: 'partial', blocked, total };
  return { closed: false, mod: 0, tier: 'none', blocked, total };
}
/** ft_stealth_round_key(): `<encounter>:r<round>` in combat, else `<encounter>:t<floor(campaign seconds / 6)>`; '' = uncached (no identity /
 *  no campaign clock). `campaignSeconds` = campaign_time_to_seconds() of the campaign's PERSISTED clock columns. */
export function stealthRoundKey(encounterId: string, inCombat: boolean, currentRound: number, campaignSeconds: number | null): string {
  if (!encounterId) return '';
  if (inCombat) return `${encounterId}:r${Math.trunc(currentRound)}`;
  if (campaignSeconds === null || !Number.isFinite(campaignSeconds)) return '';
  return `${encounterId}:t${Math.floor(Math.trunc(campaignSeconds) / 6)}`;
}

export interface StealthPairFacts {
  /** closest_box_edge_distance(get_object_box(observer), get_object_box(subject)) with the mover at the foot. */
  dist: number;
  /** the 9-ray directional sample, observer = source, subject = target volume. */
  coverBlocked: number;
  coverTotal: number;
  /** ft_stealth_sense_snapshot: deafened AND cannot see the subject. */
  noSense: boolean;
  /** ft_stealth_sense_snapshot's Attention adv_dis (≤ 0): light-driven DIS, blinded DIS. */
  advDis: number;
}
export interface StealthContest { attention: number; sneak: number; roll_id: string; adv_dis: number; fromCache: boolean }
export interface StealthWalkReaders {
  /** The CURRENT row for an id (the dice can change luck / statuses between pairs: PHP re-resolves each `other` per iteration). */
  row(id: string): Row | null;
  hidden(row: Row): boolean;
  /** ft_stealth_observer_eligible(observer, subject). */
  eligible(observer: Row, subject: Row): boolean;
  /** The pair's geometry and senses with the MOVER standing at `at`; null = the page cannot judge this pair (the walk hands back). */
  facts(observerId: string, subjectId: string, at: { x: number; y: number; z: number }): StealthPairFacts | null;
  /** The round's contest for the ordered pair — the cached one, else a fresh roll (Sneak, then Attention at `advDis`); null = hand back. */
  contest(observerId: string, subjectId: string, advDis: number): StealthContest | null;
}
export interface StealthEvaluation { observer: string; subject: string; spotted: boolean; attention: number; sneak: number; dist: number;
  distMod: number; cover: StealthCover; rollId: string; fromCache: boolean }
export type StealthWalkPlan = { kind: 'handback'; reason: string; step?: number }
  | { kind: 'resolved'; haltStep: number | null; message: string | null; moverSpottedBy: string | null;
      /** The reveals the halting foot makes, in order: [subject, observer] — the mover spotted by one observer, or every hidden creature the mover spotted. */
      reveals: Array<{ subject: string; observer: string; evaluation: StealthEvaluation }>;
      /** Every pair evaluated (a contest applied), in order — the trace a parity test compares. */
      evaluations: Array<StealthEvaluation & { step: number }> };

const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const idOf = (row: Row): string => upper((row.encounter_creatures as Row | undefined)?.encounter_creature_id);

/**
 * `feet` = legacyStepPositions(route) (feet[0] = the origin, never evaluated); `rosterIds` = the encounter's creature ids in ROSTER order
 * (query_encounter_arr: creature_number ASC, the same list the page holds); `z` = the walk's floor (a plain walk is flat).
 */
export function planStealthWalk(feet: ReadonlyArray<readonly [number, number]>, z: number, moverId: string, rosterIds: readonly string[],
  read: StealthWalkReaders): StealthWalkPlan {
  const mover = upper(moverId);
  const evaluations: Array<StealthEvaluation & { step: number }> = [];
  for (let step = 1; step < feet.length; step++) {
    const at = { x: feet[step]![0], y: feet[step]![1], z };
    const evaluate = (observer: string, subject: string): StealthEvaluation | null | 'handback' => {
      const facts = read.facts(observer, subject, at);
      if (!facts || !Number.isFinite(facts.dist) || !Number.isSafeInteger(facts.advDis)) return 'handback';
      const distMod = stealthDistanceMod(facts.dist);
      if (distMod === null) return null;
      const cover = stealthCover(facts.coverBlocked, facts.coverTotal);
      if (cover.closed) return null;
      if (facts.noSense) return null;
      const contest = read.contest(observer, subject, facts.advDis);
      if (!contest) return 'handback';
      const ev: StealthEvaluation = { observer, subject, spotted: contest.attention + distMod + cover.mod > contest.sneak, attention: contest.attention,
        sneak: contest.sneak, dist: facts.dist, distMod, cover, rollId: contest.roll_id, fromCache: contest.fromCache };
      evaluations.push({ ...ev, step });
      return ev;
    };
    const moverRow = read.row(mover);
    if (!moverRow) return { kind: 'handback', reason: 'stealth:mover-not-held', step };
    const moverHidden = read.hidden(moverRow);
    let spotter: { id: string; ev: StealthEvaluation } | null = null;
    const others: Array<{ subject: string; observer: string; evaluation: StealthEvaluation }> = [];
    for (const raw of rosterIds) {
      const otherId = upper(raw);
      if (!otherId || otherId === mover) continue;
      const other = read.row(otherId);
      if (!other) continue;
      const current = read.row(mover)!;
      if (moverHidden && spotter === null && read.eligible(other, current)) {
        const ev = evaluate(otherId, mover);
        if (ev === 'handback') return { kind: 'handback', reason: 'stealth:pair-unjudged', step };
        if (ev?.spotted) { spotter = { id: otherId, ev }; continue; }
      }
      const otherNow = read.row(otherId);
      if (spotter === null && otherNow && read.hidden(otherNow) && read.eligible(read.row(mover)!, otherNow)) {
        const ev = evaluate(mover, otherId);
        if (ev === 'handback') return { kind: 'handback', reason: 'stealth:pair-unjudged', step };
        if (ev?.spotted) others.push({ subject: otherId, observer: mover, evaluation: ev });
      }
    }
    if (spotter) return { kind: 'resolved', haltStep: step, message: STEALTH_MOVER_SPOTTED_MESSAGE, moverSpottedBy: spotter.id,
      reveals: [{ subject: mover, observer: spotter.id, evaluation: spotter.ev }], evaluations };
    if (others.length) return { kind: 'resolved', haltStep: step, message: STEALTH_OTHERS_SPOTTED_MESSAGE, moverSpottedBy: null, reveals: others, evaluations };
  }
  return { kind: 'resolved', haltStep: null, message: null, moverSpottedBy: null, reveals: [], evaluations };
}
/** idOf is exported for adapters that walk roster rows. */
export const stealthRowId = idOf;

/** The browser-decided rows a stealth walk records with its movement. */
export interface StealthWalkEffects {
  statusesAdded: import('../ability/context.js').StatusRow[];
  statusesRemoved: import('../ability/context.js').JanusCtx['statusesRemoved'];
  rows: Array<{ kind: 'reveal' | 'prompt'; roll_id: string; observer: string; subject: string; voice: boolean }>;
  lockouts: Array<{ target: string; stamped: number; until: number }>;
}
/** The wire evidence a stealth walk carries to movement_solid_truth_inc.php. */
export interface StealthWalkEvidence {
  round_key: string;
  halt_step: number | null;
  /** NEW contests this walk rolled, in roll order: the pair's own roll id, totals, the Attention DIS rolled at, and its log rows (`private`
   *  as skill_roll_arr logs them: the two contest rows secret = 2, a luck / ascension row public = 0). */
  contests: Array<{ observer: string; subject: string; roll_id: string; attention: number; sneak: number; adv_dis: number;
    rows: Array<Record<string, unknown> & { private: 0 | 1 | 2 }> }>;
  /** The round's cached contests this walk re-evaluated (the recorder binds each to the cache row it planned against). */
  cached: Array<{ observer: string; subject: string; roll_id: string }>;
  /** The halting foot's reveals: [subject, observer] (ft_stealth_reveal's arguments). */
  reveals: Array<{ subject: string; observer: string }>;
  /** movement_rev of every party but the mover, as planned against (the recorder's CAS). */
  revs: Record<string, number>;
  verdict: { ec: Record<string, Record<string, unknown>>; ecBefore: Record<string, Record<string, unknown>>;
    pc: Array<{ item_id: string; skill_id: string; column: string; before: number; after: number }> };
  effects?: StealthWalkEffects;
  /** Offered to the controlling player only after the movement is accepted. */
  fortunateOffers?: string[];
}
